import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import UploadPlaybook from '../playbook/UploadPlaybook';
import { ChatProvider } from '../chat/ChatContext';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from 'react-oidc-context';
import { getAddinChatDeployment, tryGetAddinPlaybookRulesDeployment, tryGetAddinPlaybookCompareDeployment, } from '../../services/agent';
import { classifyChatInitError, getChatInitErrorMessage, } from '../../services/chatInitError';
import { Button } from '../../ui/button';
import { AddinChat } from '../chat/AddinChat';
import { getRequiredConfig } from '../../lib/service-discovery';
import { useUserId } from '../../lib/useUserId';
import { WordAdapter } from '../../lib/host-adapters/word-adapter';
// Singleton adapter instance
const wordAdapter = new WordAdapter();
const Main = () => {
    const chatId = useMemo(() => uuidv4(), []);
    const [agentId, setAgentId] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [errorKind, setErrorKind] = useState();
    const [hasPlaybookCapabilities, setHasPlaybookCapabilities] = useState(null);
    const { user, removeUser } = useAuth();
    const { userId } = useUserId();
    useEffect(() => {
        if (!user?.access_token)
            return;
        // Fetch all deployments in parallel
        Promise.all([
            getAddinChatDeployment(user.access_token),
            tryGetAddinPlaybookRulesDeployment(user.access_token),
            tryGetAddinPlaybookCompareDeployment(user.access_token),
        ])
            .then(([chatDeployment, rulesDeployment, compareDeployment]) => {
            setAgentId(chatDeployment.pipelineId);
            // Has playbook capabilities if both rules and compare deployments exist
            setHasPlaybookCapabilities(rulesDeployment !== null && compareDeployment !== null);
            setIsLoading(false);
        })
            .catch((err) => {
            console.error('Failed to get agent deployment:', err);
            setErrorKind(classifyChatInitError(err));
            setIsLoading(false);
        });
    }, [user?.access_token]);
    if (isLoading) {
        return (_jsx("div", { className: "h-full flex flex-col items-center justify-center bg-background", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-border mx-auto mb-4" }), _jsx("p", { className: "text-sm text-foreground", children: "Loading..." })] }) }));
    }
    if (errorKind) {
        return (_jsx("div", { className: "h-full flex flex-col items-center justify-center bg-background", children: _jsxs("div", { className: "text-center p-4", children: [_jsx("p", { "data-testid": "chat-init-error", "data-error-kind": errorKind, className: "text-critical mb-4", children: getChatInitErrorMessage(errorKind, 'Word') }), errorKind === 'auth' ? (
                    // Reloading can't fix a 401/403: the client still holds the token the server
                    // rejected. Dropping the stored user sends the taskpane back to its sign-in
                    // screen (App renders AuthNavigator once isAuthenticated is false).
                    _jsx(Button, { onClick: () => removeUser(), children: "Sign in again" })) : (_jsx(Button, { onClick: () => window.location.reload(), children: "Retry" }))] }) }));
    }
    if (!agentId || !user?.access_token) {
        return (_jsx("div", { className: "h-full flex flex-col items-center justify-center bg-background", children: _jsx("div", { className: "text-center p-4", children: _jsx("p", { className: "text-foreground", children: "No chat service available" }) }) }));
    }
    // Chat-only mode: show simple AddinChat UI
    if (!hasPlaybookCapabilities) {
        const config = getRequiredConfig();
        return (_jsx(AddinChat, { agentId: agentId, bearerToken: user.access_token, apiUrl: config.apiUrl, userId: userId ?? undefined, hostAdapter: wordAdapter, greeting: "Hello! I'm here to help you with your Word document. What would you like to know?" }));
    }
    // Playbook mode: show existing UploadPlaybook UI
    return (_jsx(ChatProvider, { conversationId: chatId, agentId: agentId, children: _jsx("div", { className: "h-full flex flex-col bg-background", children: _jsx(UploadPlaybook, {}) }) }));
};
export default Main;
